import {
  forwardRef,
  type ClipboardEvent,
  type ClipboardEventHandler,
  type FocusEvent,
  type FocusEventHandler,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';
import { useCharLimit, type CharLimit } from './CharCounter';

/**
 * DESIGN §5 Inputs: Canvas Sand fill, 0.5px hairline, 6px radius, 12–14px padding.
 * Persistent MONO UPPERCASE label above the field. Required = asterisk + aria-required.
 * Error = signal-error border + message below + aria-describedby.
 */
const inputBase =
  'w-full rounded-sm border-[0.5px] border-hairline bg-canvas-sand px-[14px] py-[12px] ' +
  'font-sans text-small text-ink-graphite placeholder:text-ink-mute ' +
  'transition-[border-color] duration-almanac ease-almanac ' +
  'focus:border-accent-blue focus-visible:shadow-focus focus:outline-none ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

export interface FieldShellProps {
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  error?: string | null;
  hint?: ReactNode;
  /** Right-aligned adornment beside the label (e.g. completeness chip). */
  labelAdornment?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FieldShell({
  label,
  htmlFor,
  required,
  error,
  hint,
  labelAdornment,
  children,
  className,
}: FieldShellProps) {
  const errorId = htmlFor ? `${htmlFor}-error` : undefined;
  return (
    <div className={cn('flex flex-col gap-[6px]', className)}>
      {label && (
        <div className="flex items-center justify-between gap-sm">
          <label htmlFor={htmlFor} className="eyebrow text-ink-mute">
            {label}
            {required && (
              <span className="ms-[3px] text-signal-error" aria-hidden>
                *
              </span>
            )}
          </label>
          {labelAdornment}
        </div>
      )}
      {children}
      {hint && !error && <p className="text-small text-ink-mute">{hint}</p>}
      {error && (
        <p id={errorId} className="text-small text-signal-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Joins the ids a control is described by, dropping the absent ones. */
function describedByOf(...ids: (string | false | null | undefined)[]): string | undefined {
  return ids.filter(Boolean).join(' ') || undefined;
}

/** Chains a caller's handler after the character limit's own. */
function chain<E>(own: (e: E) => void, theirs?: (e: E) => void) {
  return (e: E) => {
    own(e);
    theirs?.(e);
  };
}

function limitProps<E extends HTMLInputElement | HTMLTextAreaElement>(
  limit: CharLimit | null,
  onPaste: ClipboardEventHandler<E> | undefined,
  onBlur: FocusEventHandler<E> | undefined,
) {
  if (!limit) return { onPaste, onBlur };
  return {
    maxLength: limit.inputProps.maxLength,
    onPaste: chain<ClipboardEvent<E>>(limit.inputProps.onPaste, onPaste),
    onBlur: chain<FocusEvent<E>>(() => limit.inputProps.onBlur(), onBlur),
  };
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  required?: boolean;
  labelAdornment?: ReactNode;
  /**
   * The schema's `maxLength`, in graphemes: shows the CharCounter under the field and stops
   * input at the limit. Needs a controlled `value`.
   */
  charLimit?: number;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, error, hint, required, labelAdornment, id, className, charLimit, onPaste, onBlur, ...rest },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  const limit = useCharLimit(String(rest.value ?? ''), charLimit);
  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      required={required}
      error={error}
      hint={hint}
      labelAdornment={labelAdornment}
    >
      <input
        ref={ref}
        id={fieldId}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedByOf(error && `${fieldId}-error`, limit?.describedBy)}
        className={cn(inputBase, error && 'border-signal-error', className)}
        {...rest}
        {...limitProps(limit, onPaste, onBlur)}
      />
      {limit?.counter}
    </FieldShell>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  required?: boolean;
  /** As on TextInput: the schema's `maxLength`, in graphemes. */
  charLimit?: number;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, required, id, className, charLimit, onPaste, onBlur, ...rest },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  const limit = useCharLimit(String(rest.value ?? ''), charLimit);
  return (
    <FieldShell label={label} htmlFor={fieldId} required={required} error={error} hint={hint}>
      <textarea
        ref={ref}
        id={fieldId}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedByOf(error && `${fieldId}-error`, limit?.describedBy)}
        className={cn(inputBase, 'min-h-[96px] resize-y', error && 'border-signal-error', className)}
        {...rest}
        {...limitProps(limit, onPaste, onBlur)}
      />
      {limit?.counter}
    </FieldShell>
  );
});

export { inputBase };
